import { Modal, Button, Typography, Stack, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  PreselectFiltersModalContentStyled,
  PreselectFiltersStyled,
} from './PreselectFiltersModal.styles';
import {
  DashboardIllustration,
  IFlow,
  ILayout,
  IDashboardComponentProperties,
  IDashboardDataGridProperties,
  DashboardComponentNames,
  useFilters,
  makeDynamicReportingPageStore,
  FiltersToolbar,
  IFilter,
} from '@diana/core';

import { REPORTING_PAGE_NS } from './i18n';
import { createPropertyToFilter } from './Support';
import { useEffect, useRef, useState } from 'react';

export const PreselectFiltersModal = ({
  isOpen,
  closeModal,
  reportId,
  displayName,
  layoutData,
  flows,
}: {
  isOpen: boolean;
  closeModal: () => void;
  reportId: string;
  displayName?: string;
  layoutData: ILayout<
    IDashboardComponentProperties | IDashboardDataGridProperties,
    DashboardComponentNames
  >;
  flows: IFlow[];
}) => {
  const { t } = useTranslation(REPORTING_PAGE_NS);
  const hasSetDefaultAvailableFilters = useRef<boolean>(false);
  const [defaultAvailableFilters, setDefaultAvailableFilters] = useState<IFilter[]>([]);
  const [defaultFilters, setDefaultFilters] = useState<string[]>([]);

  const useStore = makeDynamicReportingPageStore(reportId);
  const filters = useStore((store) => store.filters);
  const availableFilters = useStore((store) => store.availableFilters);
  const filtersValues = useStore((store) => store.filtersValues);
  const setFiltersValues = useStore((store) => store.setFiltersValues);
  const setAvailableFilters = useStore((store) => store.setAvailableFilters);
  const setFilters = useStore((store) => store.setFilters);

  const { defaultData, onFilterChange } = useFilters({
    flows,
    createPropertyToFilter,
    layoutData,
    filtersValues,
    setFiltersValues,
    availableFilters,
    withDefaultFiltersValuesFromFlow: false,
    withoutQuickFilters: true,
  });

  useEffect(() => {
    if (
      defaultData.availableFilters &&
      defaultData.filters &&
      filters.length &&
      !hasSetDefaultAvailableFilters.current
    ) {
      const filtersValuesKeys = Object.keys(filtersValues).map((key) => key);
      const availableFilters = defaultData.availableFilters.filter((filter) => {
        const propertyKey = filter.property.key;
        return defaultData.filters.includes(propertyKey) || filtersValuesKeys.includes(propertyKey);
      });

      const defaultFilters = filters
        .filter((filter) => {
          return filtersValuesKeys.includes(filter) && !defaultData.filters.includes(filter);
        })
        .concat(defaultData.filters);

      setDefaultAvailableFilters(availableFilters);
      setDefaultFilters(defaultFilters);
      hasSetDefaultAvailableFilters.current = true;
    }
  }, [filters, defaultData.availableFilters, defaultData.filters, filtersValues]);

  return (
    <Modal open={isOpen} disableAutoFocus>
      <PreselectFiltersModalContentStyled>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <DashboardIllustration width={'100%'} />
        </Box>
        <Typography fontWeight={500} mt={3}>
          {t('filtersModal.title', { displayName })}
        </Typography>
        <Typography mb={2} color="text.secondary">
          {t('filtersModal.description')}
        </Typography>
        <PreselectFiltersStyled>
          <FiltersToolbar
            filters={defaultFilters}
            setFilters={setFilters}
            availableFilters={defaultAvailableFilters}
            setAvailableFilters={setAvailableFilters}
            filtersValues={filtersValues}
            setFiltersValues={setFiltersValues}
            onFilterChange={onFilterChange}
            defaultData={defaultData}
            showAddButton={false}
            showResetButton={false}
            showRemoveButton={false}
            filtersToolbarSx={{
              backgroundColor: 'transparent',
            }}
          />
        </PreselectFiltersStyled>
        <Stack justifyContent="end" flexDirection="row" gap={1} mt={1.5}>
          <Button variant="contained" onClick={closeModal}>
            {t('filtersModal.button.done')}
          </Button>
        </Stack>
      </PreselectFiltersModalContentStyled>
    </Modal>
  );
};
